import React from 'react';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';

import Button from 'relements/build/esm/ui/button';
import Icon from 'relements/build/esm/ui/icon';
import Loader from 'relements/build/esm/ui/loader';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';
import Modal from 'relements/build/esm/overlays/modal';

import { createAlert } from '@src/actions/BotActions';
import { getDomainStats } from '@src/api/DomainAPI';
import { setURLMetadata } from '@src/actions/SubstoryActions';
import { LANGUAGE_CODE_TO_TITLE } from '@src/constants/Language';
import { AlertTypes } from '@src/constants/MogamboConstants';
import MogamboIcon from '@src/components/Icon';

import styles from './LanguageButton.scss';
import LanguageSettings from './LanguageSettings';
import TranslationSettings from './TranslationSettings';

export default class LanguageButton extends React.Component {
  _contextMenuButtonRef = React.createRef();

  state = {
    searchTerm: '',
    searchResults: [],
  };

  componentDidMount() {
    this._initializeSearch();
    this._fetchData();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.allLanguages.length !== nextProps.allLanguages.length) {
      this._initializeSearch(nextProps.allLanguages);
    }

    if (this.props.domain.title !== nextProps.domain.title) {
      this._fetchData(nextProps.domain);
    }

    if (
      this.props.status.translating_started &&
      !nextProps.status.translating_started
    ) {
      const hasError =
        nextProps.status.translating_user_says !== 2 ||
        nextProps.status.translating_bot_says !== 2;
      this.setState({ translationStatus: hasError ? 'FAIL' : 'SUCCESS' });
      setURLMetadata(undefined, undefined, true);

      if (hasError) {
        createAlert('Error translating Bot !', null, AlertTypes.NEGATIVE);
      } else {
        createAlert('Bot translated successfully', null, AlertTypes.POSITIVE);
      }
    }

    if (
      !this.props.status.translating_started &&
      nextProps.status.translating_started
    ) {
      this.setState({ translationStatus: 'IN_PROGRESS' });
    }
  }

  render() {
    return (
      <React.Fragment>
        {this.renderButton()}
        {this.renderLanguageSettingsModal()}
        {this.renderLanguageTranslationModal()}
      </React.Fragment>
    );
  }

  renderButton() {
    const { supportedLanguages, status } = this.props;
    const {
      from_language_code: fromLanguageCode = 'en',
      to_language_code: toLanguageCode = 'hi',
      translating_started: translatingStarted,
    } = status;

    const inProgressLanguage =
      toLanguageCode && translatingStarted ? [toLanguageCode] : [];

    const allItems = this.state.searchResults
      .concat(supportedLanguages)
      .concat(inProgressLanguage);

    if (this.state.translationStatus === 'IN_PROGRESS') {
      return (
        <Button
          size={Button.SIZES.BIG}
          type={Button.TYPES.OUTLINE}
          className={styles.contextMenuDropdown}
          onClick={e => this._openTranslation(e, toLanguageCode)}
        >
          <div className="center-align-flex">
            <div className={styles.loader} />
            {fromLanguageCode.toUpperCase()} → {toLanguageCode.toUpperCase()}
          </div>
        </Button>
      );
    }

    return (
      <ContextMenu.Button
        type={Button.TYPES.OUTLINE}
        prefixClassName={styles.contextMenuDropdown}
        ref={this._contextMenuButtonRef}
        size="big"
        title={this.renderTitle()}
        offset={{ top: 40, left: -100 }}
        onClose={() => this.setState({ searchTerm: '', searchResults: [] })}
      >
        <input
          value={this.state.searchTerm}
          onChange={this._search}
          placeholder="Search to find/add language"
          type="text"
          className={styles.contextMenuInput}
          autoFocus
        />
        {allItems.map(this.renderLanguage)}
        <ContextMenu.Item size="big" onClick={this._openLanguageSettings}>
          <span className={styles.blue}>Language Settings</span>
        </ContextMenu.Item>
      </ContextMenu.Button>
    );
  }

  renderLanguage = languageCode => {
    const { supportedLanguages, status } = this.props;
    const {
      to_language_code: toLanguageCode = undefined,
      translating_started: translatingStarted,
    } = status;

    const alreadyAdded = supportedLanguages.includes(languageCode);
    const inProgress = translatingStarted && toLanguageCode === languageCode;

    return (
      <ContextMenu.Item
        size="big"
        onClick={e =>
          alreadyAdded ? this._handleLanguageSelect(e, languageCode) : null
        }
        disabled={!alreadyAdded}
      >
        <span className={styles.contextMenuItem}>
          {!alreadyAdded && !inProgress ? <Icon src="plus" /> : null}
          {languageCode.toUpperCase()} - {LANGUAGE_CODE_TO_TITLE[languageCode]}
          {inProgress ? (
            <span className={styles.contextMenuItemLoader}>
              <Loader />
            </span>
          ) : null}
        </span>
      </ContextMenu.Item>
    );
  };

  renderTitle() {
    const { currentLanguage } = this.props;
    const { languageSource = 'en' } = currentLanguage;
    const { translationStatus } = this.state;

    switch (translationStatus) {
      case 'SUCCESS':
        return (
          <div className="center-align-flex">
            <MogamboIcon iconType="globe" />
            &nbsp;{languageSource.toUpperCase()}
            <div className={`${styles.dot} ${styles.green}`} />
          </div>
        );
      case 'FAIL':
        return (
          <div className="center-align-flex">
            <MogamboIcon iconType="globe" />
            &nbsp;{languageSource.toUpperCase()}
            <div className={`${styles.dot} ${styles.red}`} />
          </div>
        );
      default:
        return (
          <div className="center-align-flex">
            <MogamboIcon iconType="globe" />
            &nbsp;{languageSource.toUpperCase()}
          </div>
        );
    }
  }

  renderLanguageSettingsModal() {
    return (
      <Modal
        style={{ zIndex: 10000 }}
        active={this.state.settingsOpen}
        onOverlayClick={this._closeModal('settingsOpen')}
      >
        <Modal.Header
          onClose={this._closeModal('settingsOpen')}
          title="Languages on the Bot"
          noCta
        />
        <LanguageSettings
          supportedLanguages={this.props.supportedLanguages}
          currentLanguage={this.props.currentLanguage}
          status={this.props.status}
          botData={this.props.domain}
        />
      </Modal>
    );
  }

  renderLanguageTranslationModal() {
    const { supportedLanguages, allLanguages, domain, status } = this.props;
    return (
      <TranslationSettings
        domain={domain}
        to={this.state.translationTo}
        allLanguages={allLanguages}
        supportedLanguages={supportedLanguages}
        counts={this.state.counts}
        active={this.state.translationOpen}
        onClose={this._closeModal('translationOpen')}
        status={status}
        onChange={this.props.onChange}
      />
    );
  }

  _fetchData = async (domain = this.props.domain) => {
    if (!domain.title) return;
    const stats = (await getDomainStats(domain.title)).body;
    this.setState({
      counts: {
        nodes: stats.num_nodes,
        userSays: stats.num_user_says,
        botSays: stats.num_bot_says,
        entities: stats.num_entities,
      },
    });
  };

  _initializeSearch = (languages = this.props.allLanguages) => {
    this._Fuse = new Fuse(languages, {
      shouldSort: true,
      threshold: 0,
      location: 0,
      distance: 5,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: ['name', 'code'],
    });
  };

  _search = e => {
    const searchTerm = e.target.value;
    const searchResults = this._Fuse
      .search(searchTerm)
      .map(language => language.code)
      .filter(code => !this.props.supportedLanguages.includes(code));
    this.setState({ searchResults, searchTerm });
  };

  _handleLanguageSelect = (e, languageCode) => {
    this._contextMenuButtonRef.current.handleClose(e);
    this.props.onLanguageSelect(languageCode);
  };

  _openLanguageSettings = e => {
    this._contextMenuButtonRef.current.handleClose(e);
    this.setState({ settingsOpen: true });
  };

  _openTranslation = (e, languageCode) => {
    if (this._contextMenuButtonRef.current)
      this._contextMenuButtonRef.current.handleClose(e);
    this.setState({ translationOpen: true, translationTo: languageCode });
  };

  _closeModal = key => () => {
    this.setState({ [key]: false });
  };
}

LanguageButton.propTypes = {
  supportedLanguages: PropTypes.arrayOf(PropTypes.string),
  onLanguageSelect: PropTypes.func,
  allLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      languageCode: PropTypes.string,
      languageName: PropTypes.string,
      languageNativeName: PropTypes.string,
    })
  ),
  currentLanguage: PropTypes.shape({
    languageCode: PropTypes.string,
    languageName: PropTypes.string,
    languageNativeName: PropTypes.string,
  }),
  domain: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  status: PropTypes.shape({
    from_language_code: PropTypes.string,
    to_language_code: PropTypes.string,
    translating_bot_says: PropTypes.number,
    translating_user_says: PropTypes.number,
    translating_started: PropTypes.bool,
  }),
};

LanguageButton.defaultProps = {
  allLanguages: undefined,
  supportedLanguages: [],
  currentLanguage: {},
  onLanguageSelect: () => {},
  domain: undefined,
  status: {},
};
